import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Repeat, Hourglass, PanelLeft, Trash2, Share2 } from 'lucide-react';
import { SELECTOR_STAGES, getStageClass, getStageColor, formatTaskDate, getTagColors } from '../lib/taskGrid';
import { CalendlyDatePopover } from './CalendlyDatePopover';
import { DurationPopover } from './DurationPopover';
import { RecurrencePopover } from './RecurrencePopover';
import { UserCell, UserSelectPopover } from './UserCell';

// slice 02.1 · paso b.5a — presentacional. La franja de "etiquetas" del panel de detalle de una
// tarea (espacio + etapa/encargado/fecha/tiempo/recurrencia/tags/supervisor + los íconos de
// autoprogramar/abrir-en-pestaña/borrar). Era JSX inline de `ComponentTaskHoy` (~330 líneas).
//
// A diferencia de `TaskListView` (b.4a), acá los popovers de fecha/tiempo/usuario se importan
// directo en vez de recibirse como render-props: sólo hay un `activeTask`, no una fila por tarea,
// así que la justificación de render-props (estado por-fila) no aplica (`/refine-plans`
// 2026-09-04, ver `02.1-b5-detail-panel.md` §2).
//
// Toda mutación/toast queda en el contenedor (regla de boundary lint, ver `02.1-b5-detail-panel.md`
// §2): esta franja sólo recibe datos + `on*`.
export const TaskTagsRow = ({
    activeTask,
    canEditTask,
    canRescheduleTask,
    canDeleteTask,
    spaceName,
    userSpaces = [],
    onAssignSpace,
    systemUsers = [],
    usersToSelectFrom = [],
    activeUserSelectTaskId,
    activeUserSelectField,
    onOpenUserSelect,
    onToggleUser,
    userSearchQuery,
    onUserSearchChange,
    focusedUserOptionIdx,
    onFocusedIdxChange,
    userSelectPopoverRef,
    activeCalendlyPopoverTaskId,
    onOpenCalendly,
    popoverDurationVal,
    popoverDate,
    popoverMonth,
    popoverYear,
    onPrevMonth,
    onNextMonth,
    onCalendlyTimeSelect,
    onCalendlyDateSelect,
    activeDurationPopoverTaskId,
    onOpenDuration,
    durationPopoverRef,
    customTimeNum,
    customTimeUnit,
    onCustomTimeNumChange,
    onCustomTimeUnitChange,
    onCustomTimeSubmit,
    onPresetSelect,
    onChangeStage,
    onSaveRecurrence,
    onAddTag,
    onRemoveTag,
    dateFlash,
    onAutoSchedule,
    onDeleteTaskConfirm,
    workframeDropdown,
    onShare,
    dateConflictMessage,
}) => {
    const { t: translate } = useTranslation('tasks');
    const [assignSpaceDropdownOpen, setAssignSpaceDropdownOpen] = useState(false);
    const [showRecurrencePopover, setShowRecurrencePopover] = useState(false);
    const [tempRecurrentInterval, setTempRecurrentInterval] = useState(0);
    const [tempRecurrentUnit, setTempRecurrentUnit] = useState('weeks');

    return (
        <>
            {activeTask.space_id ? (
                <div style={{ padding: '0 0 var(--space-2) 0', marginTop: '-24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                        display: 'inline-block',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        {spaceName || 'ESPACIO'}
                    </span>
                    {workframeDropdown}
                </div>
            ) : (
                <div style={{ padding: '0 0 var(--space-2) 0', marginTop: '-24px', position: 'relative', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                        onClick={() => setAssignSpaceDropdownOpen(!assignSpaceDropdownOpen)}
                        style={{
                        display: 'inline-block',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        color: '#fca5a5',
                        border: '1px dashed rgba(239, 68, 68, 0.4)',
                        padding: 'var(--space-1) var(--space-2)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: '10px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        cursor: 'pointer'
                    }}>
                        TAREA SIN ESPACIO ▾
                    </span>
                    {workframeDropdown}
                    {assignSpaceDropdownOpen && (
                        <>
                            <div
                                style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                                onClick={() => setAssignSpaceDropdownOpen(false)}
                            />
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                marginTop: 'var(--space-1)',
                                backgroundColor: '#1f1f1f',
                                border: '1px solid #333',
                                borderRadius: 'var(--radius-md)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                zIndex: 1000,
                                minWidth: '160px',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <div style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '10px', color: '#888', borderBottom: '1px solid #333', fontWeight: 600 }}>
                                    ASIGNAR A ESPACIO
                                </div>
                                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    {userSpaces.length === 0 ? (
                                        <div style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '12px', color: '#666' }}>No hay espacios</div>
                                    ) : (
                                        userSpaces.filter(s => s.name !== 'all').map(space => (
                                            <div
                                                key={space.id}
                                                onClick={() => {
                                                    setAssignSpaceDropdownOpen(false);
                                                    onAssignSpace(space);
                                                }}
                                                style={{
                                                    padding: 'var(--space-2) var(--space-3)',
                                                    fontSize: '12px',
                                                    color: '#ddd',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    transition: 'background-color 0.2s'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                {space.name}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
            <div className="task-panel-tags-row" style={{ marginTop: '6px', marginBottom: 'var(--space-2)' }}>
                {/* Etapa Tag */}
                <div className={`task-panel-tag stage-badge-container ${getStageClass(activeTask.stage)}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span className="tag-label">{translate('modal.stageLabel')}</span>
                    <span className="stage-indicator-dot" style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getStageColor(activeTask.stage),
                        display: 'inline-block'
                    }}></span>
                    <select
                        value={activeTask.stage}
                        onChange={(e) => onChangeStage(activeTask.id, e.target.value)}
                        aria-label={translate('modal.stageAriaLabel')}
                        className={`task-panel-select-stage ${getStageClass(activeTask.stage)}`}
                        disabled={!canEditTask}
                        style={{ pointerEvents: !canEditTask ? 'none' : 'auto' }}
                    >
                        {SELECTOR_STAGES.map(s => <option key={s} value={s}>{translate(`stages.${s}`)}</option>)}
                    </select>
                </div>

                {/* Encargado Tag */}
                <div
                    className="task-panel-tag clickable-tag"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenUserSelect(activeTask, 'assignee');
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: activeUserSelectTaskId === activeTask.id && activeUserSelectField === 'assignee' ? 2500 : 1 }}
                >
                    <span className="tag-label">{translate('modal.assigneeLabel')}</span>
                    <div className="task-panel-avatar-content">
                        <UserCell
                            value={activeTask.assignee}
                            systemUsers={systemUsers}
                            onToggle={() => onOpenUserSelect(activeTask, 'assignee')}
                        />
                    </div>
                    {activeUserSelectTaskId === activeTask.id && activeUserSelectField === 'assignee' && (
                        <UserSelectPopover
                            value={activeTask.assignee}
                            options={usersToSelectFrom}
                            popoverRef={userSelectPopoverRef}
                            searchQuery={userSearchQuery}
                            onSearchChange={onUserSearchChange}
                            focusedIdx={focusedUserOptionIdx}
                            onFocusedIdxChange={onFocusedIdxChange}
                            onToggleUser={(nick) => onToggleUser(activeTask, 'assignee', nick)}
                        />
                    )}
                </div>

                {/* Fecha Tag */}
                <div
                    className="task-panel-tag clickable-tag"
                    onClick={(e) => {
                        onOpenCalendly(activeTask);
                        e.stopPropagation();
                    }}
                    style={{
                        position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px',
                        backgroundColor: dateFlash ? 'hsla(150, 85%, 70%, 0.3)' : undefined,
                        transition: 'background-color 0.5s ease',
                        zIndex: activeCalendlyPopoverTaskId === activeTask.id ? 2500 : 1
                    }}
                >
                    <Calendar size={12} style={{ color: dateFlash ? '#10b981' : '#888', transition: 'color 0.5s ease' }} />
                    <span className="tag-label" style={{ color: dateFlash ? '#10b981' : 'inherit', transition: 'color 0.5s ease' }}>{translate('modal.dateLabel')}</span>
                    <span className="tag-value-text" style={{ color: dateFlash ? '#10b981' : 'inherit', transition: 'color 0.5s ease' }}>
                        {formatTaskDate(activeTask.date)}
                        {activeTask.start_time && ` - ${new Date(activeTask.start_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`}
                    </span>
                    {activeCalendlyPopoverTaskId === activeTask.id && (
                        <CalendlyDatePopover
                            variant="panel"
                            selectedTime={popoverDurationVal}
                            onTimeSelect={(t) => onCalendlyTimeSelect(activeTask.id, t)}
                            selectedDate={popoverDate}
                            month={popoverMonth}
                            year={popoverYear}
                            onPrevMonth={onPrevMonth}
                            onNextMonth={onNextMonth}
                            onDateSelect={(d) => onCalendlyDateSelect(activeTask.id, d)}
                        />
                    )}
                    {dateConflictMessage && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            marginTop: '4px',
                            color: '#ef4444',
                            fontSize: '11px',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            fontWeight: '500'
                        }}>
                            {dateConflictMessage}
                        </div>
                    )}
                </div>

                {/* Tiempo Tag */}
                <div style={{ position: 'relative', zIndex: activeDurationPopoverTaskId === activeTask.id ? 2500 : 1 }}>
                    <div
                        className="task-panel-tag clickable-tag"
                        onClick={(e) => {
                            onOpenDuration(activeTask.id);
                            e.stopPropagation();
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                        <Clock size={12} style={{ color: '#888' }} />
                        <span className="tag-label">{translate('modal.timeLabel')}</span>
                        <span className="tag-value-text">{activeTask.tiempo || '5 min'}</span>
                    </div>
                    {activeDurationPopoverTaskId === activeTask.id && (
                        <DurationPopover
                            popoverRef={durationPopoverRef}
                            customNum={customTimeNum}
                            customUnit={customTimeUnit}
                            onCustomNumChange={onCustomTimeNumChange}
                            onCustomUnitChange={onCustomTimeUnitChange}
                            onCustomSubmit={(e) => onCustomTimeSubmit(e, activeTask.id)}
                            onPresetSelect={(preset) => onPresetSelect(activeTask.id, preset)}
                        />
                    )}
                </div>

                {/* Recurrence Tag */}
                <div
                    className="task-panel-tag clickable-tag"
                    onClick={(e) => {
                        setShowRecurrencePopover(!showRecurrencePopover);
                        if (!showRecurrencePopover) {
                            setTempRecurrentInterval(activeTask.recurrent_interval || 0);
                            setTempRecurrentUnit(activeTask.recurrent_unit || 'weeks');
                        }
                        e.stopPropagation();
                    }}
                    style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px', zIndex: showRecurrencePopover ? 2500 : 1 }}
                >
                    <Repeat size={12} style={{ color: activeTask.recurrent_interval > 0 ? '#3b82f6' : '#888' }} />
                    <span className="tag-label">{translate('modal.recurrenceLabel')}</span>
                    <span className="tag-value-text">
                        {activeTask.recurrent_interval > 0 ? translate('modal.recurrenceEvery', { interval: activeTask.recurrent_interval, unit: activeTask.recurrent_unit }) : translate('modal.recurrenceNone')}
                    </span>

                    {showRecurrencePopover && (
                        <RecurrencePopover
                            interval={tempRecurrentInterval}
                            unit={tempRecurrentUnit}
                            onIntervalChange={setTempRecurrentInterval}
                            onUnitChange={setTempRecurrentUnit}
                            onSave={() => {
                                onSaveRecurrence(activeTask.id, tempRecurrentInterval, tempRecurrentUnit);
                                setShowRecurrencePopover(false);
                            }}
                        />
                    )}
                </div>

                {/* Tags Input/Display Tag */}
                <div className="task-panel-tag tags-list-container">
                    <span className="tag-label">{translate('modal.tagsLabel')}</span>
                    <div className="task-panel-tags-list">
                        {(activeTask.tags ? activeTask.tags.split(',').map(s => s.trim()).filter(Boolean) : []).map(tag => {
                            const colors = getTagColors(tag);
                            return (
                                <span key={tag} className="task-custom-tag-pill" style={{ color: colors.color, backgroundColor: colors.backgroundColor }}>
                                    {tag}
                                    <button className="remove-tag-btn" onClick={() => onRemoveTag(tag)} style={{ color: colors.color }} aria-label={translate('modal.removeTagAriaLabel', { tag })}>×</button>
                                </span>
                            );
                        })}
                    </div>
                    <input
                        type="text"
                        className="new-tag-input-field"
                        placeholder={translate('modal.addTagPlaceholder')}
                        aria-label={translate('modal.addTagAriaLabel')}
                        onKeyDown={onAddTag}
                    />
                </div>

                {/* Supervisor Tag */}
                <div
                    className="task-panel-tag clickable-tag"
                    onClick={(e) => {
                        e.stopPropagation();
                        onOpenUserSelect(activeTask, 'supervisor');
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: activeUserSelectTaskId === activeTask.id && activeUserSelectField === 'supervisor' ? 2500 : 1 }}
                >
                    <span className="tag-label">{translate('modal.supervisorLabel')}</span>
                    <div className="task-panel-avatar-content">
                        <UserCell
                            value={activeTask.supervisor}
                            systemUsers={systemUsers}
                            onToggle={() => onOpenUserSelect(activeTask, 'supervisor')}
                        />
                    </div>
                    {activeUserSelectTaskId === activeTask.id && activeUserSelectField === 'supervisor' && (
                        <UserSelectPopover
                            value={activeTask.supervisor}
                            options={usersToSelectFrom}
                            popoverRef={userSelectPopoverRef}
                            searchQuery={userSearchQuery}
                            onSearchChange={onUserSearchChange}
                            focusedIdx={focusedUserOptionIdx}
                            onFocusedIdxChange={onFocusedIdxChange}
                            onToggleUser={(nick) => onToggleUser(activeTask, 'supervisor', nick)}
                        />
                    )}
                </div>
            </div>

            {/* Action Icons: AutoSchedule, Open Tab, Share & Delete */}
            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '12px', paddingRight: 'var(--space-2)' }}>
                <button
                    type="button"
                    title={translate('modal.shareAriaLabel')}
                    aria-label={translate('modal.shareAriaLabel')}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-1)' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onShare && onShare();
                    }}
                >
                    <Share2 size={13} />
                </button>

                {canRescheduleTask && (
                    <button
                        type="button"
                        title={translate('modal.findNextSlot')}
                        aria-label={translate('modal.findNextSlot')}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-1)' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            let durationMinutes = 60;
                            if (activeTask.tiempo) {
                                const val = parseFloat(activeTask.tiempo);
                                if (!isNaN(val)) {
                                    if (activeTask.tiempo.includes('min')) durationMinutes = val;
                                    else if (activeTask.tiempo.includes('hora')) durationMinutes = val * 60;
                                    else if (activeTask.tiempo.includes('día')) durationMinutes = val * 24 * 60;
                                    else if (activeTask.tiempo.includes('semana')) durationMinutes = val * 24 * 60 * 7;
                                }
                            }
                            onAutoSchedule(activeTask.id, durationMinutes);
                        }}
                    >
                        <Hourglass size={13} />
                    </button>
                )}

                <button
                    type="button"
                    title={translate('modal.openNewTab')}
                    aria-label={translate('modal.openNewTab')}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-1)' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/t/${activeTask.id}`, '_blank', 'width=1100,height=750,menubar=no,toolbar=no,location=no,status=no');
                    }}
                >
                    <PanelLeft size={13} />
                </button>

                {canDeleteTask && (
                    <button
                        type="button"
                        title={translate('modal.deleteTask')}
                        aria-label={translate('modal.deleteTask')}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#7f1d1d', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-1)' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteTaskConfirm();
                        }}
                    >
                        <Trash2 size={13} />
                    </button>
                )}
            </div>
        </>
    );
};

export default TaskTagsRow;
